// Auth client — talks to our Vercel serverless API.
// Backend uses cookie-based sessions + WebAuthn passkeys + magic-link fallback.
//
// Helpers exposed:
//   - getSession() — current session (or null) from /api/auth/session
//   - sendMagicLink(email) — POST /api/auth/magic-link/send
//   - signOut() — POST /api/auth/logout
//   - registerPasskey(nickname) — runs WebAuthn registration ceremony
//   - signInWithPasskey(email) — runs WebAuthn authentication ceremony
//
// Demo-mode fallback (when backend not yet wired): localStorage flag, any email signs in.

const DEMO_KEY = "df_portal_demo_session";

async function jsonFetch(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    body: opts.body ? JSON.stringify(opts.body) : undefined
  });
  let body = null;
  try { body = await res.json(); } catch {}
  return { ok: res.ok, status: res.status, body };
}

let _backendOk = null;     // null = unknown, true/false = cached probe
async function probeBackend() {
  if (_backendOk !== null) return _backendOk;
  try {
    const res = await fetch("/api/auth/session", { credentials: "include" });
    _backendOk = res.ok || res.status === 401;
    return _backendOk;
  } catch {
    _backendOk = false;
    return false;
  }
}

export async function getSession() {
  // Try real backend first
  if (await probeBackend()) {
    const { ok, body } = await jsonFetch("/api/auth/session");
    if (ok && body?.session) return body.session;
    // If backend returns null session, fall through to demo mode if set
  }
  // Demo fallback
  try {
    const raw = localStorage.getItem(DEMO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function onAuthChange(cb) {
  // Polling fallback — every 30s check session. Real impl would use server-sent events.
  const interval = setInterval(async () => {
    cb(await getSession());
  }, 30_000);
  return () => clearInterval(interval);
}

// --- Magic link ----------------------------------------------------------------

export async function sendMagicLink(email) {
  if (!email || !email.includes("@")) return { error: "Enter a valid email." };

  if (await probeBackend()) {
    const { ok, body } = await jsonFetch("/api/auth/magic-link/send", {
      method: "POST",
      body: { email }
    });
    if (!ok) return { error: body?.error || "Could not send magic link" };
    return { ok: true, mode: "magic-link" };
  }

  // Demo mode — instant local login
  try {
    localStorage.setItem(DEMO_KEY, JSON.stringify({ email, demo: true, ts: Date.now() }));
    return { ok: true, mode: "demo" };
  } catch (e) { return { error: e.message }; }
}

// --- Passkey -------------------------------------------------------------------

// Helpers: convert between base64url and ArrayBuffer for WebAuthn types
function b64uToBuf(b64u) {
  const pad = "=".repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr.buffer;
}
function bufToB64u(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Convert SimpleWebAuthn server options → native WebAuthn format
function toNativeRegistrationOpts(opts) {
  return {
    publicKey: {
      ...opts,
      challenge: b64uToBuf(opts.challenge),
      user: { ...opts.user, id: b64uToBuf(opts.user.id) },
      excludeCredentials: (opts.excludeCredentials || []).map(c => ({ ...c, id: b64uToBuf(c.id) }))
    }
  };
}
function toNativeAuthOpts(opts) {
  return {
    publicKey: {
      ...opts,
      challenge: b64uToBuf(opts.challenge),
      allowCredentials: (opts.allowCredentials || []).map(c => ({ ...c, id: b64uToBuf(c.id) }))
    }
  };
}

// Convert native PublicKeyCredential → JSON-friendly shape (base64url everywhere)
function nativeRegToJson(cred) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment,
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      attestationObject: bufToB64u(r.attestationObject),
      transports: r.getTransports?.() || []
    }
  };
}
function nativeAuthToJson(cred) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      authenticatorData: bufToB64u(r.authenticatorData),
      signature: bufToB64u(r.signature),
      userHandle: r.userHandle ? bufToB64u(r.userHandle) : null
    }
  };
}

export async function registerPasskey(nickname) {
  if (!await probeBackend()) return { error: "Backend not available — passkey registration not possible in demo mode." };
  if (!window.PublicKeyCredential) return { error: "This browser doesn't support passkeys." };

  // 1. Get options from server
  const start = await jsonFetch("/api/auth/passkey/register/start", { method: "POST" });
  if (!start.ok) return { error: start.body?.error || "Failed to start" };

  // 2. Run native ceremony
  let cred;
  try {
    cred = await navigator.credentials.create(toNativeRegistrationOpts(start.body));
  } catch (e) {
    return { error: e.name === "InvalidStateError"
      ? "This authenticator is already registered."
      : `Authenticator declined: ${e.message}` };
  }

  // 3. Send to server
  const finish = await jsonFetch("/api/auth/passkey/register/finish", {
    method: "POST",
    body: { credential: nativeRegToJson(cred), nickname: nickname || "Untitled passkey" }
  });
  if (!finish.ok) return { error: finish.body?.error || "Verification failed" };
  return { ok: true };
}

export async function signInWithPasskey(email) {
  if (!await probeBackend()) {
    // Demo mode fallback — just sign in
    try {
      localStorage.setItem(DEMO_KEY, JSON.stringify({ email, demo: true, ts: Date.now() }));
      return { ok: true, mode: "demo" };
    } catch (e) { return { error: e.message }; }
  }
  if (!window.PublicKeyCredential) return { error: "This browser doesn't support passkeys." };

  const start = await jsonFetch("/api/auth/passkey/login/start", {
    method: "POST",
    body: { email }
  });
  if (!start.ok) return { error: start.body?.error || "Failed to start" };

  let cred;
  try {
    cred = await navigator.credentials.get(toNativeAuthOpts(start.body));
  } catch (e) {
    return { error: `Authenticator declined: ${e.message}` };
  }

  const finish = await jsonFetch("/api/auth/passkey/login/finish", {
    method: "POST",
    body: { credential: nativeAuthToJson(cred), email }
  });
  if (!finish.ok) return { error: finish.body?.error || "Verification failed" };
  return { ok: true, mode: "passkey" };
}

// --- Sign out ------------------------------------------------------------------

export async function signOut() {
  if (await probeBackend()) {
    await jsonFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
  }
  localStorage.removeItem(DEMO_KEY);
}

// --- Helpers --------------------------------------------------------------------

// Returns true if the current session is a localStorage demo session
// (no real backend cookie). Used by PortalLayout to render the "Demo mode" pil
